// src/pages/Import.js
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { collection, addDoc, getDocs, doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { format } from 'date-fns';

const INTERNET_TIER_MAP = {
  'gig':            { id: 'gig',              name: 'Gig' },
  '2 gig':          { id: 'gig',              name: '2 Gig' },
  'premier':        { id: 'internet_premier', name: 'Internet (Premier)' },
  'advantage':      { id: 'internet_assist',  name: 'Internet Assist (Advantage)' },
  'internet assist':{ id: 'internet_assist',  name: 'Internet Assist' },
};

const VIDEO_TIER_MAP = {
  'stream':          { id: 'tv_choice', name: 'Stream' },
  'stream latino':   { id: 'tv_choice', name: 'Stream Latino' },
  'choice 15':       { id: 'tv_choice', name: 'TV Choice 15' },
  'select signature':{ id: 'tv_select', name: 'TV Select Signature' },
};

function mapInternetProduct(tier) {
  return INTERNET_TIER_MAP[(tier || '').toLowerCase().trim()] || null;
}
function mapVideoProduct(tier) {
  return VIDEO_TIER_MAP[(tier || '').toLowerCase().trim()] || null;
}

function parseDate(val) {
  if (!val) return null;
  if (val instanceof Date) {
    if (val.getFullYear() < 1910) return null;
    return format(val, 'yyyy-MM-dd');
  }
  if (typeof val === 'string') {
    if (val.startsWith('1901') || val.startsWith('190')) return null;
    return val.slice(0, 10);
  }
  return null;
}

function parseCoreFile(workbook, reps) {
  const ws = workbook.Sheets['Order Status'];
  if (!ws) throw new Error('Sheet "Order Status" not found. Make sure you are uploading the correct core file.');
  const rows = XLSX.utils.sheet_to_json(ws, { raw: false, defval: '', cellDates: true });
  const orders = [];

  rows.forEach(row => {
    const agentRaw  = (row['Agent - Full Name'] || '').trim();
    const agentCode = (row['Agent'] || '').trim();
    if (!agentRaw && !agentCode) return;

    const connects = parseInt(row['Sales - Connects']) || 0;
    const canceled = parseInt(row['Sales - Canceled']) || 0;
    const pending  = parseInt(row['Sales - Pending'])  || 0;

    let status = 'Written';
    if (canceled > 0) status = 'Cancelled';
    else if (connects > 0) status = 'Completed';
    else if (pending > 0) status = 'Written';

    // Pay on Internet Usage Date
    const payDate = parseDate(row['Internet Usage Date']) ||
                    parseDate(row['Date - Connect']) ||
                    parseDate(row['Date - Sales Enter Date']);

    // Written date = when the order was submitted
    const writtenDate = parseDate(row['Date - Sales Enter Date']) ||
                        parseDate(row['Date - Connect']) ||
                        payDate;

    const internetTier = (row['InternetTier - Time of Connect'] || row['InternetTier - Time of Sale'] || '').trim();
    const videoTier    = (row['VideoTier - Time of Connect']    || row['VideoTier - Time of Sale']    || '').trim();
    const psu          = (row['PSU'] || '').trim();

    const products = [];
    if (internetTier && internetTier !== 'N/A') {
      const p = mapInternetProduct(internetTier);
      if (p) products.push(p);
    }
    if (videoTier && videoTier !== 'N/A') {
      const p = mapVideoProduct(videoTier);
      if (p) products.push(p);
    }
    if (products.length === 0 && psu === 'Voice') {
      products.push({ id: 'uld_plus', name: 'Voice/ULD+' });
    }
    if (products.length === 0) return;

    const rep = reps.find(r =>
      (r.agentCode && r.agentCode === agentCode) ||
      r.name?.toLowerCase() === agentRaw.toLowerCase()
    );

    // Internet Usage "N" = not using service (col AI)
    const internetUsageFlag = (row['Internet Usage'] || '').toString().trim().toUpperCase();
    const hasUsage = internetUsageFlag === 'Y' || (payDate && payDate !== '1901-01-01');
    const locationType = (row['Location Type'] || '').trim();
    const nonPayable = locationType.toLowerCase() === 'secondary';

    products.forEach(product => {
      orders.push({
        customerName:    (row['Customer Full Name'] || '').replace(',', ' ').trim(),
        productId:       product.id,
        productName:     product.name,
        repName:         rep?.name || agentRaw,
        repId:           rep?.id   || null,
        agentCode,
        status,
        date:            payDate,
        writtenDate:     writtenDate,
        disconnectDate:  parseDate(row['PSU - Disconnect Date']),
        hasUsage,
        internetUsageFlag,
        locationType,
        nonPayable,
        address:         [row['SVC Unit - Address Line 1'], row['SVC Unit - City'], row['SVC Unit - State']].filter(Boolean).join(', '),
        workOrderNumber: row['Work Order Number'] || '',
        accountNumber:   row['Customer - Account Number'] || '',
        fiscalMonth:     row['Fiscal Month'] || '',
        carrier:         'Spectrum',
        fileType:        'core',
        source:          'csv_import',
      });
    });
  });

  return orders;
}

function parseMobileFile(workbook, reps) {
  const ws = workbook.Sheets['Mobile'];
  if (!ws) throw new Error('Sheet "Mobile" not found. Make sure you are uploading the correct mobile file.');
  const rows = XLSX.utils.sheet_to_json(ws, { raw: false, defval: '', cellDates: true });

  // Group by account to determine line 1 vs 2-10
  const accountGroups = {};
  rows.forEach(row => {
    const acct = row['Customer - Account Number'] || row['Mobile Account Number'];
    if (!acct) return;
    if (!accountGroups[acct]) accountGroups[acct] = [];
    accountGroups[acct].push(row);
  });

  const orders = [];

  rows.forEach(row => {
    const agentRaw  = (row['Agent - Full Name'] || '').trim();
    const agentCode = (row['Agent'] || '').trim();
    if (!agentRaw && !agentCode) return;

    // Only pay on activated lines
    const activated = (row['Network Activation'] || '').trim().toUpperCase();
    if (activated !== 'Y') return;

    // Pay on Network Activation Date
    const payDate = parseDate(row['Network Activation Date']) ||
                    parseDate(row['Date - Connect']);

    // Written date = when the order was entered
    const writtenDate = parseDate(row['Date - Sales Enter Date']) ||
                        parseDate(row['Date - Connect']) ||
                        payDate;

    const canceled = parseInt(row['Sales - Canceled']) || 0;
    const connects = parseInt(row['Sales - Connects']) || 0;
    const status = canceled > 0 ? 'Cancelled' : connects > 0 ? 'Completed' : 'Written';

    const acct = row['Customer - Account Number'] || row['Mobile Account Number'];
    const group = accountGroups[acct] || [];
    const lineIndex = group.indexOf(row);
    const productId   = lineIndex === 0 ? 'mobile_line1' : 'mobile_lines2to10';
    const productName = lineIndex === 0 ? 'Mobile Line 1' : 'Mobile Lines 2–10';

    const rep = reps.find(r =>
      (r.agentCode && r.agentCode === agentCode) ||
      r.name?.toLowerCase() === agentRaw.toLowerCase()
    );

    // Network Activation "N" = not activated (col AE)
    const networkActivationFlag = (row['Network Activation'] || '').toString().trim().toUpperCase();
    const hasUsage = networkActivationFlag === 'Y' && payDate && payDate !== '1900-01-02' && payDate !== '2000-01-02';

    orders.push({
      customerName:    (row['Customer Full Name'] || '').replace(',', ' ').trim(),
      productId,
      productName,
      repName:         rep?.name || agentRaw,
      repId:           rep?.id   || null,
      agentCode,
      status,
      date:            payDate,
      writtenDate:     writtenDate,
      hasUsage,
      networkActivationFlag,
      address:         [row['SVC Unit - Address Line 1'], row['SVC Unit - City'], row['SVC Unit - State']].filter(Boolean).join(', '),
      workOrderNumber: row['Mobile Work Order Number'] || '',
      accountNumber:   acct || '',
      fiscalMonth:     row['Fiscal Month'] || '',
      deviceModel:     row['Line Device Model'] || '',
      ratePlan:        row['Line Rate Plan'] || '',
      saleType:        row['Sale Type'] || '',
      carrier:         'Spectrum',
      fileType:        'mobile',
      source:          'csv_import',
    });
  });

  return orders;
}

function calcCommission(order, reps, compPlans) {
  const rep = reps.find(r => r.id === order.repId);
  if (!rep) return { commissions: [], totalCommission: 0 };
  const plan = compPlans.find(p => p.id === (rep.compPlanId || 'synergy_1_31_26'));
  if (!plan) return { commissions: [], totalCommission: 0 };
  const rates = plan.rates?.[order.productId] || {};
  const roleKey = (rep.role || 'Agent').replace(' ', '');
  const amount = rates[roleKey] || 0;
  const commissions = amount > 0 ? [{ repId: rep.id, repName: rep.name, role: rep.role || 'Agent', amount }] : [];
  const rm = reps.find(r => r.role === 'RM' && r.teamId === rep.teamId);
  if (rm) {
    const rmAmount = rates['RM'] || 0;
    if (rmAmount > 0) commissions.push({ repId: rm.id, repName: rm.name, role: 'RM', amount: rmAmount });
  }
  return { commissions, totalCommission: commissions.reduce((a, c) => a + c.amount, 0) };
}

export default function ImportPage() {
  const [reps, setReps] = useState([]);
  const [compPlans, setCompPlans] = useState([]);
  const [coreFile, setCoreFile] = useState(null);
  const [mobileFile, setMobileFile] = useState(null);
  const [corePreview, setCorePreview] = useState(null);
  const [mobilePreview, setMobilePreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    async function load() {
      const [repSnap, planSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'compPlans')),
      ]);
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCompPlans(planSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    load();
  }, []);

  function handleFile(type, e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const wb = XLSX.read(ev.target.result, { type: 'array', cellDates: true });
      try {
        if (type === 'core') {
          setCoreFile(file);
          setCorePreview(parseCoreFile(wb, reps));
        } else {
          setMobileFile(file);
          setMobilePreview(parseMobileFile(wb, reps));
        }
      } catch(err) {
        alert(err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  async function runImport() {
    const allOrders = [...(corePreview || []), ...(mobilePreview || [])];
    if (!allOrders.length) return;
    setImporting(true);
    setResult(null);

    // Load all existing orders to check for duplicates by work order number
    const existingSnap = await getDocs(collection(db, 'orders'));
    const existingMap = {}; // workOrderNumber+productId -> { docId, status, date }
    existingSnap.docs.forEach(d => {
      const data = d.data();
      const key = (data.workOrderNumber || '') + '|' + (data.productId || '');
      if (key !== '|') existingMap[key] = { docId: d.id, status: data.status, date: data.date };
    });

    let created = 0, updated = 0, skipped = 0;
    const batch = writeBatch(db);
    let batchCount = 0;

    for (const order of allOrders) {
      const { commissions, totalCommission } = calcCommission(order, reps, compPlans);
      const key = (order.workOrderNumber || '') + '|' + (order.productId || '');
      const existing = key !== '|' ? existingMap[key] : null;

      if (existing) {
        // Check if anything changed
        const statusChanged = existing.status !== order.status;
        const dateChanged = order.date && order.date !== '1901-01-01' && existing.date !== order.date;

        if (statusChanged || dateChanged) {
          // Update existing order
          const updates = { status: order.status };
          if (dateChanged) updates.date = order.date;
          if (order.hasUsage !== undefined) updates.hasUsage = order.hasUsage;
          if (order.writtenDate) updates.writtenDate = order.writtenDate;
          // Recalculate commission if rep is now matched
          if (commissions.length > 0) {
            updates.commissions = commissions;
            updates.totalCommission = totalCommission;
          }
          batch.update(doc(db, 'orders', existing.docId), updates);
          batchCount++;
          updated++;
        } else {
          skipped++;
        }
      } else {
        // Create new order
        const newRef = doc(collection(db, 'orders'));
        batch.set(newRef, {
          ...order,
          commissions,
          totalCommission,
          overrides: order.repId ? [{ repId: order.repId, role: 'Rep' }] : [],
          quantity: 1,
          createdAt: serverTimestamp(),
        });
        batchCount++;
        created++;
      }

      // Commit in batches of 400
      if (batchCount >= 400) {
        await batch.commit();
        batchCount = 0;
      }
    }

    if (batchCount > 0) await batch.commit();

    setResult({ created, updated, skipped, core: corePreview?.length || 0, mobile: mobilePreview?.length || 0 });
    setImporting(false);
    setCorePreview(null); setMobilePreview(null);
    setCoreFile(null); setMobileFile(null);
  }

  const allOrders = [...(corePreview || []), ...(mobilePreview || [])];
  const unmatched = [...new Set(allOrders.filter(o => !o.repId).map(o => o.agentCode || o.repName))];
  const STATUS_COLORS = { Completed: '#00d4a0', Cancelled: '#ff6b6b', Written: '#5599cc' };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Import</h1>
        <p style={styles.sub}>Upload your Spectrum exports — pays on Internet Usage Date (core) and Network Activation Date (mobile)</p>
      </div>

      <div style={styles.uploadGrid}>
        {[
          { type: 'core', label: 'Core products', sub: 'Internet · TV · Voice', hint: 'Synergy_Pending_Canceled_Connects_Disconnects_*.xlsx', file: coreFile, preview: corePreview },
          { type: 'mobile', label: 'Mobile', sub: 'Mobile lines · pays on activation', hint: 'Synergy_Mobile_*.xlsx', file: mobileFile, preview: mobilePreview },
        ].map(({ type, label, sub, hint, file, preview }) => (
          <div key={type} style={styles.card}>
            <div style={styles.cardTitle}>{label}</div>
            <div style={styles.cardSub}>{sub}</div>
            <div style={styles.uploadArea} onClick={() => document.getElementById(`${type}-input`).click()}>
              <div style={styles.uploadIcon}>↑</div>
              <div style={styles.uploadText}>{file ? file.name : hint}</div>
              <div style={styles.uploadHint}>Click to upload .xlsx</div>
              <input id={`${type}-input`} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={e => handleFile(type, e)} />
            </div>
            {preview && (
              <div style={styles.previewBox}>
                <span style={{ color: '#00d4a0' }}>{preview.filter(o => o.status === 'Completed').length} completed</span>
                {' · '}
                <span style={{ color: '#5599cc' }}>{preview.filter(o => o.status === 'Written').length} pending</span>
                {' · '}
                <span style={{ color: '#ff6b6b' }}>{preview.filter(o => o.status === 'Cancelled').length} cancelled</span>
                <div style={{ marginTop: 4, color: '#555', fontSize: 11 }}>{preview.length} total orders ready</div>
              </div>
            )}
          </div>
        ))}
      </div>

      {allOrders.length > 0 && (
        <>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Preview — first 10 rows</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>{['Customer', 'Product', 'Rep', 'Status', 'Pay date', 'Type'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {allOrders.slice(0, 10).map((o, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{o.customerName}</td>
                      <td style={{ ...styles.td, color: '#888' }}>{o.productName}</td>
                      <td style={styles.td}>{o.repId ? o.repName : <span style={{ color: '#ff6b6b' }}>⚠ {o.repName}</span>}</td>
                      <td style={styles.td}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, color: STATUS_COLORS[o.status], background: STATUS_COLORS[o.status] + '22' }}>{o.status}</span></td>
                      <td style={{ ...styles.td, color: '#666' }}>{o.date || '—'}</td>
                      <td style={{ ...styles.td, color: '#555', fontSize: 11 }}>{o.fileType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {unmatched.length > 0 && (
            <div style={styles.warnCard}>
              <div style={styles.warnTitle}>⚠ {unmatched.length} agent(s) not matched to a rep in your system</div>
              <div style={styles.warnSub}>Orders will import but commissions won't calculate until you add these reps and set their agent code in the Reps page.</div>
              <div style={styles.warnCodes}>{unmatched.join(', ')}</div>
            </div>
          )}

          <div style={styles.importRow}>
            {result && (
              <div style={styles.resultBox}>
                ✓ {result.created} new · {result.updated} updated · {result.skipped} unchanged
                <span style={{ color: '#555', marginLeft: 8, fontSize: 11 }}>({result.core} core + {result.mobile} mobile processed)</span>
              </div>
            )}
            <button style={styles.importBtn} onClick={runImport} disabled={importing}>
              {importing ? 'Importing...' : `Import ${allOrders.length.toLocaleString()} orders`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 13, color: '#555', margin: '4px 0 0' },
  uploadGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  card: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '20px 24px', marginBottom: 16 },
  cardTitle: { fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 },
  cardSub: { fontSize: 12, color: '#555', marginBottom: 16 },
  uploadArea: { border: '1px dashed #2a2a3a', borderRadius: 10, padding: '28px 20px', textAlign: 'center', cursor: 'pointer' },
  uploadIcon: { fontSize: 24, color: '#00d4a0', marginBottom: 8 },
  uploadText: { fontSize: 12, color: '#888', marginBottom: 4, wordBreak: 'break-all' },
  uploadHint: { fontSize: 11, color: '#444' },
  previewBox: { marginTop: 14, background: '#0f0f18', borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#888' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '8px 12px', color: '#555', fontWeight: 500, borderBottom: '1px solid #1e1e2a', whiteSpace: 'nowrap', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '9px 12px', color: '#ccc', borderBottom: '1px solid #111', whiteSpace: 'nowrap' },
  warnCard: { background: '#2a1e0a', border: '1px solid #5a3e10', borderRadius: 10, padding: '14px 18px', marginBottom: 16 },
  warnTitle: { fontSize: 13, color: '#f5a623', fontWeight: 500, marginBottom: 4 },
  warnSub: { fontSize: 12, color: '#888', marginBottom: 8, lineHeight: 1.5 },
  warnCodes: { fontSize: 11, color: '#666', fontFamily: 'monospace' },
  importRow: { display: 'flex', alignItems: 'center', gap: 14 },
  importBtn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '11px 28px', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  resultBox: { background: '#1a2e24', border: '1px solid #2a5a3a', borderRadius: 8, padding: '10px 16px', color: '#00d4a0', fontSize: 13 },
};
